import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OfflineNotificationProps {
  className?: string;
}

export const OfflineNotification: React.FC<OfflineNotificationProps> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowNotification(true);
      // Masquer la notification après 3 secondes
      setTimeout(() => setShowNotification(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Vérifier l'état initial
    if (!navigator.onLine) {
      setShowNotification(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showNotification) return null;

  return (
    <div className={`fixed top-4 right-4 z-50 transition-all duration-300 ${className}`}>
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
          isOnline
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-orange-50 border-orange-200 text-orange-800'
        }`}
      >
        {isOnline ? (
          <Wifi className="w-5 h-5 text-green-600" />
        ) : (
          <WifiOff className="w-5 h-5 text-orange-600" />
        )}
        <div className="flex flex-col">
          <span className="font-medium text-sm">
            {isOnline ? t('offlineNotification.connectedTitle') : t('offlineNotification.disconnectedTitle')}
          </span>
          <span className="text-xs opacity-75">
            {isOnline
              ? t('offlineNotification.syncing')
              : t('offlineNotification.limitedFeatures')}
          </span>
        </div>
        <button
          onClick={() => setShowNotification(false)}
          className="ml-2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default OfflineNotification;
